/**
 * What language the document is actually in at the moment an error is reported.
 *
 * Chrome and Edge translate a page by rewriting its text nodes and wrapping each one in a
 * `<font>` element. React holds references to the nodes it rendered, so once the translator
 * has moved them those references point somewhere they no longer belong, and the next insert
 * or remove throws `insertBefore` / `removeChild` ... `not a child of this node`. The error
 * itself says nothing about why, and the RUM SDK never fills its own `device_locale` fields
 * (0 of 308k production error events carried one, measured 18/09/2026), so without this there
 * is no way to tell a translated page apart from a genuine render bug.
 *
 * `fontNodes` is the decisive field: `<font>` is deprecated HTML that this codebase never
 * renders, so any non-zero count is the translator's work and not ours.
 */
function describeDocumentLanguage(): Record<string, unknown> {
  try {
    if (typeof document === 'undefined') {
      return {};
    }

    return {
      fontNodes: document.getElementsByTagName('font').length,
      htmlLang: document.documentElement.lang || null,
      uiLang: typeof navigator === 'undefined' ? null : navigator.language,
    };
  } catch {
    // This runs inside the error path it is describing; it must never add a second failure.
    return {};
  }
}

/**
 * Context for a reported error.
 *
 * `kind` is the one field with a job: it lands in `error_kind`, the column an O2 alert can
 * group and threshold on. Use a stable, greppable value, prefixed by the feature
 * (`serena_chat_load_failed`, not "load failed"), because an alert is written against that
 * string and a typo makes the error invisible rather than noisy.
 */
export type CaptureContext = Record<string, unknown> & { kind?: string };

/**
 * Manually report a handled error to OpenObserve RUM — the O2 replacement for
 * `Sentry.captureException`. Unhandled errors are captured automatically by the RUM
 * SDK; use this for errors you catch and still want recorded (e.g. in an error
 * boundary or a rejected request).
 *
 * No-op on the server (RUM is browser-only) and inert until `initOpenObserveRum`
 * has activated the SDK. Never throws — telemetry must not break the app.
 */
export function captureException(error: unknown, context?: CaptureContext): void {
  if (typeof window === 'undefined') {
    return;
  }

  const err = error instanceof Error ? error : new Error(typeof error === 'string' ? error : JSON.stringify(error));

  // `kind` becomes the error's `name`, and the RUM SDK sends `name` as `error_kind`. Anything
  // put in `context` instead travels as a custom attribute, which is **not** what an alert can
  // filter on: measured on 2026-09-21, `error_kind` was empty on 50,777 of 50,777 errors from
  // the `candidature` pages, so the only way to find one of ours was a substring search on the
  // message. A stable kind is what makes an error alertable rather than merely findable.
  if (context?.kind) {
    err.name = context.kind;
  }

  // Caller context is spread last so an explicit key always wins over the snapshot.
  const reportedContext = { ...describeDocumentLanguage(), ...context };

  // Load the browser-only RUM SDK lazily so this module never pulls it into the server
  // bundle (the SDK's CommonJS core `require`s the ESM-only `uuid`, which crashes SSR).
  // The chunk is already cached once RUM has initialized in the browser.
  import('@openobserve/browser-rum')
    .then(({ openobserveRum }) => {
      openobserveRum.addError(err, reportedContext);
    })
    .catch(() => {
      // Swallow: RUM not initialized / SDK failure must never surface to the user.
    });
}
